import {
  Copy,
  Flex,
  Link,
  Square,
  Squish,
  Stack,
  SvgCaretLeft,
} from '@grubhubprod/gh-cookbook-react/dist';
import {
  Breakpoint,
  useDeviceSize,
} from '@grubhubprod/umami-library/dist/hooks/useDeviceSize';
import { publish } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import Logo from 'src/components/Logo/Logo';
import NavBarLinksContainer, {
  INavRoute,
} from 'src/components/Navigation/NavBarLinksWrapper';
import { throttle } from 'src/utilities/throttle-utility';
import { getWindow } from 'src/utilities/window';
import { MultiCorpDropdown } from 'src/components/MultiCorpDropdown/MultiCorpDropdown';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';
import { matchPath } from './NavBar.utils';

const DESKTOP_NAV_HEIGHT = '60px';
export const MOBILE_NAV_HEIGHT = '48px';
const MOBILE_ADMIN_NAV_HEIGHT = '84px';
const DESKTOP_MARGING_LEFT = '24px';
const MOBILE_MARGING_LEFT = '12px';

// TODO: Can be removed when cleaning up the FeatureFlag.ENABLE_BALANCE_UI flag
const legacyManagementRoutes: INavRoute[] = [
  { text: 'Home', page: 'home' },
  { text: 'Order activity', page: 'orderHistory' },
  { text: 'Line of credit', page: 'lineOfCredit' },
  { text: 'People', page: 'people' },
  { text: 'Your account', page: 'account' },
];

const managementRoutes: INavRoute[] = [
  { text: 'Home', page: 'home' },
  { text: 'Order activity', page: 'orderHistory' },
  { text: 'Meal credits', page: 'lineOfCredit' },
  { text: 'People', page: 'people' },
  { text: 'Your account', page: 'account' },
];

const HIDE_NAV_FOR_ROUTES = [
  '/corporate/management/[corpid]/orderHistory',
  '/corporate/management/[corpid]/people',
  '/corporate/management/[corpid]/groups/[groupId]',
];

export default function NavBar(): JSX.Element {
  const window = getWindow();
  const { breakpoint } = useDeviceSize(window);
  const isTablet = breakpoint > Breakpoint.sm;
  const isDesktop = breakpoint >= Breakpoint.lg;
  const prevScrollPos = useRef(0);
  const [visible, setVisible] = useState(true);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isCorpDropdownOpen, setIsCorpDropdownOpen] = useState(false);
  const {
    enterpriseData: { isInternalAdminUser },
    allUserCorps,
  } = useAuth();
  const { corpid } = useParams<{ corpid: string }>();
  const { pathname } = useLocation();
  const { value: isBalanceUiEnabled } = useFeatureFlag(
    FeatureFlag.ENABLE_BALANCE_UI
  );

  const hasPremiumCorps = allUserCorps && allUserCorps.PREMIUM.length > 0;
  const hasMultipleBasicCorps = allUserCorps && allUserCorps.BASIC.length > 1;
  const hasBasicCorps = allUserCorps && allUserCorps.BASIC.length > 0;

  const showCorpsDropdown =
    hasMultipleBasicCorps || (hasBasicCorps && hasPremiumCorps);

  const navBarTotalHeight = isInternalAdminUser
    ? `-${MOBILE_ADMIN_NAV_HEIGHT}`
    : `-${MOBILE_NAV_HEIGHT}`;

  const hideNavbarOnScroll = () => {
    if (!isDesktop && matchPath(HIDE_NAV_FOR_ROUTES, pathname)) {
      const currentScrollPos = window.pageYOffset;
      const lastVisible = visible;
      const isNavBarVisible =
        // do not handle scrolling during the height of the navbar
        currentScrollPos > parseInt(navBarTotalHeight, 10)
          ? prevScrollPos.current > currentScrollPos
          : true;
      setVisible(isNavBarVisible);
      publish('navBar:hidden', { isNavBarVisible });
      let lastScrollPos = currentScrollPos;
      // need to make sure added/removed HTML content from DOM does not affect next scroll handling after the direction change
      if (lastVisible && !isNavBarVisible) {
        lastScrollPos -= window.pageYOffset;
      }
      if (!lastVisible && isNavBarVisible) {
        lastScrollPos += window.pageYOffset;
      }
      prevScrollPos.current = lastScrollPos <= 0 ? 0 : lastScrollPos;
    }
  };

  const handleScroll = throttle(hideNavbarOnScroll, 100);

  function onMenuToggle(isMenuOpen: boolean) {
    setMenuOpen(isMenuOpen);
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevScrollPos, visible, breakpoint]);

  return (
    <nav
      data-testid="navbar"
      className="navBarFixed"
      style={{
        top: visible || isMenuOpen ? '0' : navBarTotalHeight,
      }}
    >
      <Flex bg="globals.white" flexDirection="column">
        {isInternalAdminUser && (
          <Squish inset="sm">
            <Flex alignItems="center">
              <Copy variant="body" color="interactive">
                <SvgCaretLeft size="16px" />
              </Copy>
              <Link href={`${window.location.origin}/groupadmin/corps/`}>
                Corporate accounts
              </Link>
            </Flex>
          </Squish>
        )}

        <Flex
          bg="globals.white"
          justifyContent="space-between"
          alignContent="center"
          className="navBarBottom"
          borderTop="1px solid"
          borderTopColor="lightLine"
          style={{
            height: isTablet ? DESKTOP_NAV_HEIGHT : MOBILE_NAV_HEIGHT,
          }}
        >
          <Flex alignItems="center">
            <Square
              ml={isTablet ? DESKTOP_MARGING_LEFT : MOBILE_MARGING_LEFT}
              as="header"
              backgroundColor="globals.white"
            >
              <Logo />
            </Square>
          </Flex>
          <Flex
            alignItems="center"
            flexDirection={isTablet ? 'row' : 'row-reverse'}
          >
            {(isTablet || !isCorpDropdownOpen) && (
              <NavBarLinksContainer
                routes={
                  isBalanceUiEnabled ? managementRoutes : legacyManagementRoutes
                }
                onMenuToggle={onMenuToggle}
              />
            )}
            {!isInternalAdminUser && showCorpsDropdown && !isMenuOpen && (
              <Stack x={isTablet ? 'md' : 'xxx-sm'}>
                <MultiCorpDropdown
                  isMobile={!isTablet}
                  hasBasicCorps={hasBasicCorps}
                  corpId={corpid as string}
                  hasPremiumCorps={hasPremiumCorps}
                  allUserCorps={allUserCorps}
                  openDropdownCb={(isOpen) => setIsCorpDropdownOpen(isOpen)}
                />
              </Stack>
            )}
          </Flex>
        </Flex>
      </Flex>
    </nav>
  );
}
